import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { IProjectAsset, Resolution, AssetType, ScriptCharacter, ScriptProp, ScriptScene } from '@packages/global';
import { episodeScriptApi } from '../../../services/user/episodeScript.api';
import { convertAudioToMp3 } from '../../../utils/audioConverter';

export interface MvSelectedModels {
  llmModelId: string;
  llmVlModelId: string;
  text2imageModelId: string;
  imageEditModelId: string;
}

export function useMvGeneration(projectId?: string) {
  const navigate = useNavigate();

  // 音频文件
  const [audioFileName, setAudioFileName] = useState('');
  const [audioUrl, setAudioUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const audioInputRef = useRef<HTMLInputElement>(null);

  // prompt
  const [prompt, setPrompt] = useState('');

  // 生成选项
  const [selectedSubjects, setSelectedSubjects] = useState<IProjectAsset[]>([]);
  const [selectedStyle, setSelectedStyle] = useState('');
  const [aspectRatio, setAspectRatio] = useState('16:9');
  const [resolution, setResolution] = useState<Resolution>(Resolution.HD_720P);
  const [maxShotDuration, setMaxShotDuration] = useState<number | null>(null);
  const [selectedModels, setSelectedModels] = useState<MvSelectedModels>({
    llmModelId: '',
    llmVlModelId: '',
    text2imageModelId: '',
    imageEditModelId: '',
  });

  const [loading, setLoading] = useState(false);

  // 上传音频
  const handleAudioUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowed = ['.mp3', '.wav', '.m4a', '.aac', '.flac'];
    const ext = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
    if (!allowed.includes(ext)) { alert('不支持的格式，请上传 MP3 / WAV / M4A / AAC / FLAC'); return; }
    if (file.size > 50 * 1024 * 1024) { alert('文件大小不能超过 50MB'); return; }

    setUploading(true);
    setAudioFileName(file.name);
    try {
      // wav/flac 体积大，先在浏览器端转换为 mp3 再上传
      const uploadFile = await convertAudioToMp3(file, ({ stage, message }) => {
        console.log(`[AudioConvert] ${stage}: ${message}`);
      });

      const formData = new FormData();
      formData.append('audio', uploadFile);
      const res = await fetch('/api/user/upload/audio', {
        method: 'POST',
        body: formData,
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      const result = await res.json();
      if (result.success && result.data?.url) {
        setAudioUrl(result.data.url);
      } else {
        throw new Error(result.message || '上传失败');
      }
    } catch (err: any) {
      alert(err.message || '上传失败，请重试');
      setAudioFileName('');
    } finally {
      setUploading(false);
      if (audioInputRef.current) audioInputRef.current.value = '';
    }
  };

  const handleClearAudio = () => { setAudioFileName(''); setAudioUrl(''); };

  /** 从「我的音乐」直接选取，跳过上传流程 */
  const setAudioFromMusic = (name: string, url: string) => {
    setAudioFileName(name);
    setAudioUrl(url);
  };

  // 主体管理
  const addSubjects = (assets: IProjectAsset[]) => {
    setSelectedSubjects(prev => {
      const newAssets = assets.filter(a => !prev.some(p => p.id === a.id));
      return [...prev, ...newAssets];
    });
  };
  const removeSubject = (id: string) => setSelectedSubjects(prev => prev.filter(a => a.id !== id));

  // 提交：调用 episodeScriptApi.createEpisode，传入 audioUrl 触发 MV 模式，成功后立即跳转
  const handleSubmit = async () => {
    if (!audioUrl) return;
    setLoading(true);
    try {
      // 按 assetType 分组，分别映射到对应的 Script 类型
      // 保留 assetId（系统库主体标识）和 sourceAssetId（血缘来源），确保后续 ProjectAsset 记录里可追溯
      const characters: ScriptCharacter[] = selectedSubjects
        .filter(a => a.assetType === AssetType.CHARACTER)
        .map(a => ({
          name: a.name,
          description: a.description,
          prompt: a.prompt || '',
          imageUrl: a.conceptImageUrl,
          audioUrl: a.voiceReference,
          voiceId: a.voiceModelId,
          assetId: a.assetId,
          sourceAssetId: a.id,
        }));

      const props: ScriptProp[] = selectedSubjects
        .filter(a => a.assetType === AssetType.PROP)
        .map(a => ({
          name: a.name,
          description: a.description,
          prompt: a.prompt || '',
          imageUrl: a.conceptImageUrl,
          assetId: a.assetId,
          sourceAssetId: a.id,
        }));

      const scenes: ScriptScene[] = selectedSubjects
        .filter(a => a.assetType === AssetType.SCENE)
        .map(a => ({
          name: a.name,
          description: a.description,
          prompt: a.prompt || '',
          imageUrl: a.conceptImageUrl,
          location: a.location,
          assetId: a.assetId,
          sourceAssetId: a.id,
        }));

      const res = await episodeScriptApi.createEpisode({
        prompt: prompt || `MV 生成：${audioFileName}`,
        projectId: projectId || undefined,
        audioUrl,
        audioName: audioFileName,
        aspectRatio: aspectRatio as any,
        resolution,
        llmModelId: selectedModels.llmModelId || undefined,
        llmVlModelId: selectedModels.llmVlModelId || undefined,
        imageModelId: selectedModels.text2imageModelId || undefined,
        characters: characters.length > 0 ? characters : undefined,
        props: props.length > 0 ? props : undefined,
        scenes: scenes.length > 0 ? scenes : undefined,
        artStyle: selectedStyle || undefined,
        maxShotDuration: maxShotDuration ?? undefined,
      });
      // 立即跳转，音频分析在生成任务内部执行（Step 0）
      navigate(`/episode/${res.episodeId}/script-generation`);
    } catch (err: any) {
      alert(err.message || '提交失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  return {
    audioFileName, audioUrl, uploading, audioInputRef,
    handleAudioUpload, handleClearAudio, handleSubmit, setAudioFromMusic,
    prompt, setPrompt,
    selectedSubjects, addSubjects, removeSubject,
    selectedStyle, setSelectedStyle,
    aspectRatio, setAspectRatio,
    resolution, setResolution,
    maxShotDuration, setMaxShotDuration,
    selectedModels, setSelectedModels,
    loading,
    canSubmit: !!audioUrl && !uploading && !loading,
  };
}
